import React from 'react';
import { camelize } from 'humps';
import classNames from 'classnames';
import I18n from 'i18n-js';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { RCCheckbox, RCIcon, RCImage, RCThemedBox } from '@reverbdotcom/cadence/components';

import QuickPriceEditButton, {
  VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT,
} from '../listings/quick_price_edit/quick_price_edit_button';
import SellerListingCardDetails, { listingAge } from './SellerListingCardDetails';
import SellerListingCardFooter from './SellerListingCardFooter';
import BLANK_MEDIUM_IMAGE from '../../../images/products/blank_medium.jpg';
import PublishErrors from './PublishErrors';
import LoadingOverlay from '../../shared/loading_overlay';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { SellerListing, ActionType } from './sellerListingsCollectionReducer';
import SellerListingCardTradeInFooter from './SellerListingCardTradeInFooter';
import SellerListingCardActionMenu from './SellerListingCardActionMenu';
import RenewListingButton from '../listings/RenewListingButton';
import { ArrowClockwiseIcon } from '@reverbdotcom/cadence/icons/react';

const SUSPENDED = 'suspended';
const LIVE = 'live';

export function ListingState({ listing, isRelistEnabled }: { listing: SellerListing, isRelistEnabled: boolean }) {
  const isLive = listing.state === LIVE;
  const isSuspended = listing.state === SUSPENDED;

  const { state } = listing;

  return (
    <div className="d-flex fx-align-center gap-3 mobile-fx-dir-col mobile-fx-align-start mobile-gap-2">
      <div className={classNames('seller-listing-card-footer__state', {
        'seller-listing-card-footer__state--default': !isLive && !isSuspended,
        'seller-listing-card-footer__state--live': isLive,
        'seller-listing-card-footer__state--suspended': isSuspended,
      })}>
        {I18n.t(
          `discovery.listingsManagement.sellerListingCard.states.${camelize(
            state,
          )}`,
        ).toUpperCase()}
      </div>
      {!!listing.publishedAt?.seconds && (
        isRelistEnabled ?
          <RCThemedBox
            themeType="utility"
            variant="notice-ultralight"
            className="seller-listing-card__age-badge"
          >
            {listingAge(listing.publishedAt.seconds)}
          </RCThemedBox> :
          <div className="size-90 text-color-secondary weight-semibold">
            {listingAge(listing.publishedAt.seconds)}
          </div>
      )}
    </div>
  );
}

export const COMPONENT_NAME = 'SellerListingCard';

export default function SellerListingCard({
  listing,
  showQuickPriceEditPopover = false,
}: {
  listing: SellerListing;
  showQuickPriceEditPopover?: boolean;
}) {

  const user = useUser();
  const openNewTab = user && user.deviceName === WEB;

  const {
    listingsCollectionState,
    dispatch: selectionDispatch,
  } = React.useContext(SellerListingsCollectionContext);

  const isSelected = !!listingsCollectionState.selectedListings.find(selectedListing => selectedListing.id == listing.id);

  function handleSelection() {
    if (isSelected) {
      selectionDispatch({
        type: ActionType.DESELECT_LISTING,
        payload: {
          listing,
        },
      });
    } else {
      selectionDispatch({
        type: ActionType.SELECT_LISTING,
        payload: {
          listing,
        },
      });
    }
  }

  const isRelistEnabled = listing.renewalEligibility.isEligible;
  const relistDescription = listing.priceRecommendation?.priceMiddle?.display ?
    I18n.t(
      'discovery.listingsManagement.renewListing.renewPriceRecDescription',
      { displayPrice: listing.priceRecommendation?.priceMiddle?.display },
    ) : I18n.t('discovery.listingsManagement.renewListing.renewDescription');

  return (
    <div className="seller-listing-card">
      <LoadingOverlay showOverlay={listing.loading}>
        <div className="seller-listing-card__content">
          <div className="d-flex fx-justify-between tablet-fx-dir-col tablet-gap-3 width-100">
            <div className="d-flex width-60 gap-4">
              <RCCheckbox
                checked={isSelected}
                onChange={handleSelection}
                label={listing.title}
                visuallyHiddenLabel
              />

              <div className="seller-listing-card__content__image">
                <CoreLink
                  to={listingItemPagePath(listing)}
                  target={openNewTab ? '_blank' : undefined}
                >
                  <RCImage
                    src={listing.images[0]?.source || BLANK_MEDIUM_IMAGE}
                    imageBox="fullWidth"
                    alt={listing.title}
                  />
                </CoreLink>
              </div>
              <div className="d-flex fx-dir-col gap-2 width-60 tablet-width-100">
                <ListingState listing={listing} isRelistEnabled={isRelistEnabled} />
                <CoreLink
                  className="seller-listing-card__content__listing-title"
                  to={listingItemPagePath(listing)}
                  target={openNewTab ? '_blank' : undefined}
                >
                  <span style={{ fontSize: 'var(--rc-font-size-400)' }}>{listing.title}</span>
                </CoreLink>

                <div>
                  <SellerListingCardDetails listing={listing} />
                  <PublishErrors listing={listing} />
                </div>
              </div>
            </div>
            <div className="d-flex fx-dir-col fx-align-end">
              <div className="d-flex fx-dir-row fx-align-start gap-2 mobile-pl-5">
                {isRelistEnabled &&
                  <RenewListingButton
                    listing={listing}
                  />
                }
                <QuickPriceEditButton
                  listing={listing}
                  showQuickPriceEditPopover={showQuickPriceEditPopover}
                  isEditable={VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT.includes(listing.state)}
                  hidePriceRecommendationCallout={Boolean(isRelistEnabled && listing.priceRecommendation?.priceMiddle?.display)}
                />
                <SellerListingCardActionMenu listing={listing} />
              </div>
              {isRelistEnabled &&
                <div className="d-flex gap-2 mt-3 max-width-70 mobile-max-width-100">
                  <span style={{ color: 'var(--rc-color-accent-loud)' }}><RCIcon size="rc-space-4" icon={<ArrowClockwiseIcon />} /></span>
                  <div className="" style={{ fontSize: 'var(--rc-font-size-350)' }}>
                    {relistDescription}
                  </div>
                </div>
              }
            </div>
          </div>
        </div>

        <SellerListingCardFooter listing={listing}/>
        <SellerListingCardTradeInFooter listing={listing} />
      </LoadingOverlay>
    </div>
  );
}
